/**
 * Schema Column Clustering
 *
 * Implements hierarchical agglomerative clustering for grouping
 * schema columns by semantic similarity.
 *
 * Algorithm: HAC with average linkage
 * Features: TF-IDF + name tokens + data type + domain keywords
 * Performance: < 300ms for 50 columns
 */

import { ColumnInfo, ColumnCluster, ClusteringConfig } from '../../types';

// ==================== TYPES ====================

export interface ClusteringOptions {
  similarityThreshold?: number;  // Default: 0.25
  minClusterSize?: number;       // Default: 1
  maxClusters?: number;          // Default: 10
  maxClusterSize?: number;       // Default: 8 - prevents giant clusters
  respectUserClusters?: boolean; // Default: true
}

export interface ClusteringResult {
  clusters: ColumnCluster[];
  config: ClusteringConfig;
}

// ==================== DOMAIN KEYWORDS ====================

const DOMAIN_KEYWORDS: Record<string, string[]> = {
  'Demographics': [
    'age', 'gender', 'sex', 'race', 'ethnicity', 'education', 'income',
    'occupation', 'employment', 'marital', 'nationality', 'birth', 'population'
  ],
  'Temporal': [
    'date', 'time', 'duration', 'period', 'year', 'month', 'day', 'week',
    'timestamp', 'start', 'end', 'interval', 'frequency', 'timeline'
  ],
  'Outcomes': [
    'outcome', 'result', 'effect', 'response', 'success', 'failure',
    'mortality', 'survival', 'improvement', 'recovery', 'efficacy', 'performance'
  ],
  'Methodology': [
    'method', 'protocol', 'procedure', 'technique', 'approach', 'design',
    'sample', 'experiment', 'trial', 'study', 'analysis', 'measurement'
  ],
  'Identification': [
    'id', 'identifier', 'name', 'title', 'label', 'code', 'key',
    'reference', 'index', 'number', 'serial'
  ],
  'Quantitative': [
    'count', 'amount', 'quantity', 'total', 'sum', 'average', 'mean',
    'score', 'rate', 'ratio', 'percentage', 'value', 'measure'
  ],
  'Location': [
    'location', 'place', 'site', 'region', 'country', 'city', 'address',
    'geographic', 'area', 'zone', 'territory'
  ],
  'Classification': [
    'type', 'category', 'class', 'group', 'status', 'level', 'stage',
    'grade', 'rank', 'tier'
  ]
};

// Cluster colors (from Tailwind palette)
const CLUSTER_COLORS = [
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
  '#F97316', // orange
  '#6366F1', // indigo
];

// ==================== FEATURE EXTRACTION ====================

/**
 * Tokenize a column name by splitting on underscores, camelCase, and hyphens
 */
function tokenizeName(name: string): string[] {
  return name
    .replace(/([a-z])([A-Z])/g, '$1 $2')  // camelCase
    .replace(/[_-]/g, ' ')                 // underscores and hyphens
    .toLowerCase()
    .split(/\s+/)
    .filter(token => token.length > 1);    // Remove single chars
}

/**
 * Extract tokens from text (definition, rationale)
 */
function tokenizeText(text: string): string[] {
  if (!text) return [];
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')  // Remove punctuation
    .split(/\s+/)
    .filter(token => token.length > 2 && !STOP_WORDS.has(token));
}

// Common stop words to ignore
const STOP_WORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'been',
  'be', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
  'should', 'may', 'might', 'must', 'shall', 'can', 'need', 'dare', 'ought',
  'used', 'this', 'that', 'these', 'those', 'which', 'who', 'whom', 'whose',
  'what', 'where', 'when', 'why', 'how', 'all', 'each', 'every', 'both',
  'few', 'more', 'most', 'other', 'some', 'such', 'than', 'too', 'very',
  'just', 'also', 'now', 'here', 'there', 'then', 'once', 'only', 'own',
  'same', 'so', 'not', 'nor', 'any', 'into', 'over', 'after', 'before'
]);

/**
 * Find terms that appear in too many columns (not discriminative)
 */
function findCommonTerms(columns: ColumnInfo[], threshold: number = 0.5): Set<string> {
  const termCount = new Map<string, number>();

  columns.forEach(col => {
    const tokens = new Set(tokenizeName(col.name));
    tokens.forEach(token => {
      termCount.set(token, (termCount.get(token) || 0) + 1);
    });
  });

  const commonTerms = new Set<string>();
  const minAppearances = Math.ceil(columns.length * threshold);

  termCount.forEach((count, term) => {
    if (count >= minAppearances) {
      commonTerms.add(term);
    }
  });

  return commonTerms;
}

/**
 * Compute TF-IDF scores for all columns, excluding overly common terms
 */
function computeTFIDF(
  columns: ColumnInfo[],
  excludeTerms: Set<string> = new Set()
): Map<string, Map<string, number>> {
  // Document frequency for each term
  const df = new Map<string, number>();

  // Term frequency per column
  const columnTokens = columns.map(col => {
    const tokens = [
      ...tokenizeName(col.name),
      ...tokenizeText(col.definition || ''),
      ...tokenizeText(col.rationale || '')
    ].filter(t => !excludeTerms.has(t)); // Filter out common terms

    // Count term frequency
    const tf = new Map<string, number>();
    tokens.forEach(token => {
      tf.set(token, (tf.get(token) || 0) + 1);
    });

    // Update document frequency
    tf.forEach((_, token) => {
      df.set(token, (df.get(token) || 0) + 1);
    });

    return { column: col, tf, totalTokens: tokens.length };
  });

  // Compute TF-IDF
  const n = columns.length;
  const tfidf = new Map<string, Map<string, number>>();

  columnTokens.forEach(({ column, tf, totalTokens }) => {
    const scores = new Map<string, number>();

    tf.forEach((freq, token) => {
      const termFreq = freq / Math.max(totalTokens, 1);
      const docFreq = df.get(token) || 1;
      const idf = Math.log(n / docFreq) + 1;
      scores.set(token, termFreq * idf);
    });

    tfidf.set(column.name, scores);
  });

  return tfidf;
}

/**
 * Check if column matches a domain category
 */
function matchesDomain(column: ColumnInfo, keywords: string[]): boolean {
  const text = `${column.name} ${column.definition || ''} ${column.rationale || ''}`.toLowerCase();
  return keywords.some(kw => text.includes(kw));
}

/**
 * Extract feature vector for a column
 */
function extractFeatures(
  column: ColumnInfo,
  tfidf: Map<string, Map<string, number>>,
  vocabulary: string[]
): number[] {
  const features: number[] = [];

  // 1. TF-IDF features (normalized)
  const columnTfidf = tfidf.get(column.name) || new Map();
  vocabulary.forEach(term => {
    features.push(columnTfidf.get(term) || 0);
  });

  // 2. Data type features (one-hot)
  const dataTypes = ['string', 'number', 'boolean', 'date', 'array'];
  dataTypes.forEach(dt => {
    features.push(column.data_type?.toLowerCase() === dt ? 1 : 0);
  });

  // 3. Has allowed values (categorical indicator)
  features.push(column.allowed_values && column.allowed_values.length > 0 ? 2 : 0);

  // 4. Domain category features
  Object.keys(DOMAIN_KEYWORDS).forEach(category => {
    features.push(matchesDomain(column, DOMAIN_KEYWORDS[category]) ? 2 : 0);
  });

  return features;
}

// ==================== SIMILARITY ====================

/**
 * Compute cosine similarity between two vectors
 */
function cosineSimilarity(a: number[], b: number[]): number {
  if (a.length !== b.length || a.length === 0) return 0;

  let dotProduct = 0;
  let normA = 0;
  let normB = 0;

  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }

  const denominator = Math.sqrt(normA) * Math.sqrt(normB);
  return denominator === 0 ? 0 : dotProduct / denominator;
}

/**
 * Compute similarity matrix for all columns
 */
function computeSimilarityMatrix(
  columns: ColumnInfo[],
  features: Map<string, number[]>
): number[][] {
  const n = columns.length;
  const matrix: number[][] = Array(n).fill(null).map(() => Array(n).fill(0));

  for (let i = 0; i < n; i++) {
    matrix[i][i] = 1; // Self-similarity
    for (let j = i + 1; j < n; j++) {
      const featA = features.get(columns[i].name) || [];
      const featB = features.get(columns[j].name) || [];
      const sim = cosineSimilarity(featA, featB);
      matrix[i][j] = sim;
      matrix[j][i] = sim;
    }
  }

  return matrix;
}

// ==================== HIERARCHICAL CLUSTERING ====================

interface ClusterNode {
  members: number[];  // Indices into columns array
  similarity: number; // Intra-cluster similarity
}

/**
 * Compute average linkage distance between two clusters
 */
function averageLinkage(
  clusterA: ClusterNode,
  clusterB: ClusterNode,
  simMatrix: number[][]
): number {
  let totalSim = 0;
  let count = 0;

  for (const i of clusterA.members) {
    for (const j of clusterB.members) {
      totalSim += simMatrix[i][j];
      count++;
    }
  }

  return count > 0 ? totalSim / count : 0;
}

/**
 * Hierarchical agglomerative clustering with average linkage
 * Now with max cluster size constraint
 */
function hierarchicalClustering(
  columns: ColumnInfo[],
  simMatrix: number[][],
  threshold: number,
  maxClusterSize: number = 8
): ClusterNode[] {
  // Initialize: each column is its own cluster
  let clusters: ClusterNode[] = columns.map((_, i) => ({
    members: [i],
    similarity: 1
  }));

  // Merge until threshold is reached
  while (clusters.length > 1) {
    // Find most similar cluster pair
    let maxSim = -1;
    let mergeI = -1;
    let mergeJ = -1;

    for (let i = 0; i < clusters.length; i++) {
      for (let j = i + 1; j < clusters.length; j++) {
        // Skip if merged cluster would exceed max size
        if (clusters[i].members.length + clusters[j].members.length > maxClusterSize) {
          continue;
        }

        const sim = averageLinkage(clusters[i], clusters[j], simMatrix);
        if (sim > maxSim) {
          maxSim = sim;
          mergeI = i;
          mergeJ = j;
        }
      }
    }

    // Stop if no valid merge found or best similarity is below threshold
    if (mergeI === -1 || mergeJ === -1 || maxSim < threshold) {
      break;
    }

    // Merge clusters i and j
    const merged: ClusterNode = {
      members: [...clusters[mergeI].members, ...clusters[mergeJ].members],
      similarity: maxSim
    };

    // Remove merged clusters and add new one
    clusters = clusters.filter((_, idx) => idx !== mergeI && idx !== mergeJ);
    clusters.push(merged);
  }

  return clusters;
}

// ==================== CLUSTER LABELING ====================

/**
 * Capitalize first letter of each word
 */
function titleCase(str: string): string {
  return str.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Ensure a cluster name is unique by trying alternatives or adding numeric suffix
 */
function ensureUniqueName(
  baseName: string,
  usedNames: Set<string>,
  columns: ColumnInfo[],
  commonTerms: Set<string>
): string {
  if (!usedNames.has(baseName)) {
    return baseName;
  }

  // Try alternative names from columns (excluding common terms)
  for (const col of columns) {
    const altTokens = tokenizeName(col.name).filter(t => !commonTerms.has(t) && t.length > 2);
    if (altTokens.length >= 2) {
      const altName = titleCase(altTokens.slice(0, 2).join(' '));
      if (!usedNames.has(altName)) {
        return altName;
      }
    }
  }

  // Add numeric suffix as last resort
  let suffix = 2;
  while (usedNames.has(`${baseName} ${suffix}`)) suffix++;
  return `${baseName} ${suffix}`;
}

/**
 * Generate a label for a cluster based on discriminative terms
 * STRICTLY excludes common terms that appear across the entire dataset
 */
function generateClusterLabel(
  columns: ColumnInfo[],
  memberIndices: number[],
  commonTerms: Set<string> = new Set()
): string {
  const members = memberIndices.map(i => columns[i]);

  // Collect ALL discriminative tokens from all members (excluding common terms)
  const tokenFreq = new Map<string, number>();
  const tokenInColumns = new Map<string, Set<number>>();

  members.forEach((member, idx) => {
    // STRICTLY filter out common terms
    const tokens = tokenizeName(member.name).filter(t => !commonTerms.has(t) && t.length > 2);
    const seenInThisColumn = new Set<string>();

    tokens.forEach(token => {
      tokenFreq.set(token, (tokenFreq.get(token) || 0) + 1);
      if (!seenInThisColumn.has(token)) {
        seenInThisColumn.add(token);
        if (!tokenInColumns.has(token)) {
          tokenInColumns.set(token, new Set());
        }
        tokenInColumns.get(token)!.add(idx);
      }
    });
  });

  // Score tokens by coverage across cluster members
  const tokenScores: Array<{ token: string; score: number; coverage: number }> = [];

  tokenInColumns.forEach((columnSet, token) => {
    const coverage = columnSet.size / members.length;
    const score = coverage * (tokenFreq.get(token) || 0);
    tokenScores.push({ token, score, coverage });
  });

  // Sort by coverage first, then by score
  tokenScores.sort((a, b) => {
    if (Math.abs(a.coverage - b.coverage) > 0.1) {
      return b.coverage - a.coverage;
    }
    return b.score - a.score;
  });

  // Get top 2 distinct tokens for the label
  const topTokens: string[] = [];
  for (const { token, coverage } of tokenScores) {
    if (topTokens.length >= 2) break;
    if (coverage >= 0.25 || topTokens.length === 0) {
      topTokens.push(token);
    }
  }

  if (topTokens.length > 0) {
    return titleCase(topTokens.join(' & '));
  }

  // If no discriminative tokens found, use a descriptive fallback based on member count
  // Never fall back to common terms
  return `Group ${memberIndices[0] + 1}`;
}

// ==================== MAIN CLUSTERING FUNCTION ====================

/**
 * Cluster schema columns using hierarchical agglomerative clustering
 */
export function clusterColumns(
  columns: ColumnInfo[],
  existingClusters?: ColumnCluster[],
  options: ClusteringOptions = {}
): ClusteringResult {
  const {
    similarityThreshold = 0.25,  // Lower threshold for better separation
    minClusterSize = 1,
    maxClusters = 10,
    maxClusterSize = 8,  // Prevent giant clusters
    respectUserClusters = true
  } = options;

  if (columns.length === 0) {
    return {
      clusters: [],
      config: {
        enabled: true,
        clusters: [],
        unclustered_behavior: 'show_at_end'
      }
    };
  }

  // Separate user-assigned columns from algorithm-assigned
  const userClusters = existingClusters?.filter(c => c.id.startsWith('user_')) || [];
  const userAssignedColumns = new Set(
    respectUserClusters ? userClusters.flatMap(c => c.column_names) : []
  );

  const columnsToCluster = columns.filter(c => !userAssignedColumns.has(c.name));

  if (columnsToCluster.length === 0) {
    return {
      clusters: userClusters,
      config: {
        enabled: true,
        clusters: userClusters,
        unclustered_behavior: 'show_at_end'
      }
    };
  }

  // Find overly common terms (appear in >50% of columns) - these are not discriminative
  const commonTerms = findCommonTerms(columnsToCluster, 0.5);

  // Compute TF-IDF excluding common terms
  const tfidf = computeTFIDF(columnsToCluster, commonTerms);

  // Build vocabulary (union of all terms, excluding common ones)
  const vocabulary = new Set<string>();
  tfidf.forEach(scores => {
    scores.forEach((_, term) => vocabulary.add(term));
  });
  const vocabArray = Array.from(vocabulary);

  // Extract features for each column
  const features = new Map<string, number[]>();
  columnsToCluster.forEach(col => {
    features.set(col.name, extractFeatures(col, tfidf, vocabArray));
  });

  // Compute similarity matrix
  const simMatrix = computeSimilarityMatrix(columnsToCluster, features);

  // Perform hierarchical clustering with max cluster size constraint
  const clusterNodes = hierarchicalClustering(
    columnsToCluster,
    simMatrix,
    similarityThreshold,
    maxClusterSize
  );

  // Convert to ColumnCluster format with unique names
  const usedNames = new Set<string>();
  const algorithmClusters: ColumnCluster[] = clusterNodes
    .filter(node => node.members.length >= minClusterSize)
    .slice(0, maxClusters)
    .map((node, idx) => {
      const baseName = generateClusterLabel(columnsToCluster, node.members, commonTerms);
      const memberColumns = node.members.map(i => columnsToCluster[i]);
      const name = ensureUniqueName(baseName, usedNames, memberColumns, commonTerms);
      usedNames.add(name);

      return {
        id: `algo_${Date.now()}_${idx}`,
        name,
        description: `${node.members.length} columns (similarity: ${(node.similarity * 100).toFixed(0)}%)`,
        color: CLUSTER_COLORS[idx % CLUSTER_COLORS.length],
        collapsed: false,
        column_names: node.members.map(i => columnsToCluster[i].name)
      };
    });

  // Handle unclustered columns (singleton clusters below threshold)
  const clusteredColumns = new Set(algorithmClusters.flatMap(c => c.column_names));
  const unclustered = columnsToCluster.filter(c => !clusteredColumns.has(c.name));

  if (unclustered.length > 0) {
    // Group unclustered columns into smaller chunks if too many
    const unclusteredChunks: ColumnInfo[][] = [];
    for (let i = 0; i < unclustered.length; i += maxClusterSize) {
      unclusteredChunks.push(unclustered.slice(i, i + maxClusterSize));
    }

    unclusteredChunks.forEach((chunk, chunkIdx) => {
      const chunkIndices = chunk.map(col =>
        columnsToCluster.findIndex(c => c.name === col.name)
      ).filter(idx => idx !== -1);

      // Generate name strictly excluding common terms
      let baseName = chunk.length === 1
        ? titleCase(tokenizeName(chunk[0].name).filter(t => !commonTerms.has(t) && t.length > 2).slice(0, 3).join(' '))
        : generateClusterLabel(columnsToCluster, chunkIndices, commonTerms);

      // Ensure we have a valid name (no common terms allowed)
      if (!baseName) baseName = `Group ${algorithmClusters.length + chunkIdx + 1}`;

      // Ensure unique name
      const clusterName = ensureUniqueName(baseName, usedNames, chunk, commonTerms);
      usedNames.add(clusterName);

      algorithmClusters.push({
        id: `algo_${Date.now()}_other_${chunkIdx}`,
        name: clusterName,
        description: `${chunk.length} columns`,
        color: CLUSTER_COLORS[(algorithmClusters.length + chunkIdx) % CLUSTER_COLORS.length],
        collapsed: false,
        column_names: chunk.map(c => c.name)
      });
    });
  }

  // Merge with user clusters
  const allClusters = [...userClusters, ...algorithmClusters];

  return {
    clusters: allClusters,
    config: {
      enabled: true,
      clusters: allClusters,
      unclustered_behavior: 'show_at_end'
    }
  };
}

/**
 * Suggest a cluster for a new/edited column
 */
export function suggestClusterForColumn(
  column: ColumnInfo,
  existingClusters: ColumnCluster[],
  allColumns: ColumnInfo[],
  threshold: number = 0.3
): ColumnCluster | null {
  if (existingClusters.length === 0) return null;

  // Find common terms
  const commonTerms = findCommonTerms(allColumns, 0.5);

  // Compute TF-IDF including the new column
  const columnsWithNew = [...allColumns, column];
  const tfidf = computeTFIDF(columnsWithNew, commonTerms);

  // Build vocabulary
  const vocabulary = new Set<string>();
  tfidf.forEach(scores => {
    scores.forEach((_, term) => vocabulary.add(term));
  });
  const vocabArray = Array.from(vocabulary);

  // Extract features for new column
  const newFeatures = extractFeatures(column, tfidf, vocabArray);

  // Find most similar cluster
  let bestCluster: ColumnCluster | null = null;
  let bestSimilarity = threshold;

  existingClusters.forEach(cluster => {
    // Compute average similarity to cluster members
    const memberColumns = allColumns.filter(c => cluster.column_names.includes(c.name));
    if (memberColumns.length === 0) return;

    let totalSim = 0;
    memberColumns.forEach(member => {
      const memberFeatures = extractFeatures(member, tfidf, vocabArray);
      totalSim += cosineSimilarity(newFeatures, memberFeatures);
    });

    const avgSim = totalSim / memberColumns.length;
    if (avgSim > bestSimilarity) {
      bestSimilarity = avgSim;
      bestCluster = cluster;
    }
  });

  return bestCluster;
}

/**
 * Get an unused color from the palette, avoiding colors already used by existing clusters
 */
function getUnusedColor(existingClusters: ColumnCluster[]): string {
  const usedColors = new Set(existingClusters.map(c => c.color));

  // Find the first unused color
  for (const color of CLUSTER_COLORS) {
    if (!usedColors.has(color)) {
      return color;
    }
  }

  // If all colors are used, pick the least recently used one (based on array position)
  // This ensures deterministic behavior
  return CLUSTER_COLORS[existingClusters.length % CLUSTER_COLORS.length];
}

/**
 * Create a user-defined cluster
 */
export function createUserCluster(
  name: string,
  columnNames: string[],
  existingClusters: ColumnCluster[] = [],
  color?: string
): ColumnCluster {
  return {
    id: `user_${Date.now()}`,
    name,
    description: `${columnNames.length} columns (user-defined)`,
    color: color || getUnusedColor(existingClusters),
    collapsed: false,
    column_names: columnNames
  };
}

/**
 * Assign a column to a cluster (user override)
 */
export function assignColumnToCluster(
  columnName: string,
  targetClusterId: string | null,
  currentClusters: ColumnCluster[]
): ColumnCluster[] {
  // Remove column from all clusters first, but DON'T filter out empty clusters yet
  // (the target cluster might be empty and we need to add the column to it first)
  let updatedClusters = currentClusters.map(cluster => ({
    ...cluster,
    column_names: cluster.column_names.filter(name => name !== columnName)
  }));

  if (targetClusterId === null) {
    // Create new user cluster for this column
    updatedClusters.push(createUserCluster(`Custom: ${columnName}`, [columnName], updatedClusters));
  } else {
    // Add to existing cluster
    updatedClusters = updatedClusters.map(cluster =>
      cluster.id === targetClusterId
        ? {
            ...cluster,
            column_names: [...cluster.column_names, columnName],
            // Mark as user-modified if it was algorithm-generated
            id: cluster.id.startsWith('algo_') ? `user_${cluster.id}` : cluster.id
          }
        : cluster
    );
  }

  // NOW filter out empty clusters (after column was added to target)
  return updatedClusters.filter(cluster => cluster.column_names.length > 0);
}

/**
 * Re-cluster when schema changes, preserving user clusters
 */
export function reclusterOnSchemaChange(
  columns: ColumnInfo[],
  existingClusters: ColumnCluster[],
  changedColumnNames: string[],
  options?: ClusteringOptions
): ClusteringResult {
  // User clusters are preserved
  const userClusters = existingClusters.filter(c => c.id.startsWith('user_'));

  // Re-cluster all algorithm-assigned columns
  return clusterColumns(columns, userClusters, {
    ...options,
    respectUserClusters: true
  });
}
